import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ExportIcon from "components/icons/ExportIcon";
import Modal from "components/modal/Modal";
import style from "components/reports/erasure-reports-table.scss";
import ExportReportDialog, { FormValues } from "components/reports/export/ExportReportDialog";
import { CustomReportViewFilterGroup } from "domain/reports";
import { reportExportService } from "services/report/ReportExportService";
import "services/report/ReportViewService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import buttonStyle from "styles/buttons.scss";
import buttons from "styles/buttons.scss";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export interface Props {
    columns?: string[];
    startDate?: string;
    endDate?: string;
    searchQuery: string;
    isAllReportsView: boolean;
    filters?: CustomReportViewFilterGroup;
    exportAllButton?: boolean;
    reportUuids?: string[];
    openExportDialog?: boolean;
    onSelectedReportsExport?: (invoked: boolean) => void;
    hasOnlyBmsReports?: boolean;
    totalReportCount?: number;
    disableExportReportButton?: boolean;
}
const ExportReportsView = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [visible, setVisible] = React.useState<boolean>(false);
    const hide = () => {
        setVisible(false);
    };
    const [typeFormat, setTypeFormat] = React.useState("");
    const errorText = {
        title: "",
    };
    const [visibleProcess, setVisibleProcess] = React.useState<boolean>(false);
    const hideProcess = () => {
        setVisibleProcess(false);
    };

    const submitEventHandler = async ({
        format,
        startDate,
        endDate,
        search,
        templateUuid,
        columns,
        filters,
        language,
        reportUuids,
        filenameColumns,
        filenameSeparator,
        combined,
        humanReadableNumbers,
        localizedColumns,
        subRecordSeparator,
        recordSeparator,
        publicLinkExpiration,
    }: FormValues) => {
        const abortController = new AbortController();
        try {
            if (publicLinkExpiration) {
                usageStatisticsService.sendEvent({
                    category: Category.REPORTS,
                    action: Action.EXPORT_REPORTS_WITH_PUBLIC_LINKS,
                });
            }
            await reportExportService.exportProcessingView(
                format,
                search,
                templateUuid,
                abortController,
                combined,
                humanReadableNumbers,
                localizedColumns,
                startDate,
                endDate,
                columns,
                filters,
                language,
                reportUuids,
                filenameColumns,
                recordSeparator,
                subRecordSeparator,
                filenameSeparator,
                publicLinkExpiration
            );
            setVisible(false);
            setVisibleProcess(true);
            setTypeFormat(format);
        } catch (e) {
            errorText.title = e;
            logger.error("Failed to export reports: ", e);
            return;
        }
    };

    React.useEffect(() => {
        if (props.openExportDialog) {
            setVisible(props.openExportDialog);
        }
    }, [props.openExportDialog]);

    React.useEffect(() => {
        if (props.onSelectedReportsExport) {
            props.onSelectedReportsExport(false);
        }
    }, [visible]);

    const modalTitle = props.reportUuids?.length
        ? t("ErasureReport.exportTitle", { reportCount: props.reportUuids?.length })
        : t("ErasureReport.exportMessage.title");

    return (
        <>
            {props.exportAllButton && (
                <button
                    data-html={true}
                    onClick={() => {
                        setVisible(true);
                    }}
                    className={buttons.secondaryButton}
                    data-testid={testIds.workArea.report.primaryView.table.exportButton}
                    disabled={props.disableExportReportButton}
                >
                    <ExportIcon color={theme.iconFillColor} />
                    {t("ErasureReport.exportMessage.title")}
                </button>
            )}

            <Modal isOpen={visible} hideModal={hide} modalTitle={modalTitle}>
                <ExportReportDialog
                    handle={submitEventHandler}
                    columns={props.columns}
                    startDate={props.startDate}
                    endDate={props.endDate}
                    search={props.searchQuery}
                    onShowModal={setVisible}
                    isAllReportsView={props.isAllReportsView}
                    filters={props.filters}
                    reportUuids={props.reportUuids}
                    hasOnlyBmsReports={props.hasOnlyBmsReports}
                    totalReportCount={props.totalReportCount}
                    singleReportExport={false}
                />
            </Modal>
            <Modal
                isOpen={visibleProcess}
                hideModal={hideProcess}
                modalTitle={t("ErasureReport.exportProcessing.title")}
            >
                <div className={classNames(style.processDialog, style.filters)}>
                    <div>{t("ErasureReport.exportProcessing.message", { type: typeFormat })}</div>
                    <div className={style.marginAdjust}>
                        <strong>{t("Common.note")}</strong>
                        {t("ErasureReport.exportProcessing.noteMessage")}
                    </div>
                    <div className={classNames(style.exportButtonContainer, style.cancelButton)}>
                        <button
                            className={buttonStyle.primaryOkButton}
                            data-testid={testIds.common.dialog.closeButton}
                            onClick={() => setVisibleProcess(false)}
                        >
                            {t("Common.ok")}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ExportReportsView;
